import React from 'react';
import { times } from 'lodash';
import I18n from 'i18n-js';
import OverflowingRow from '@reverbdotcom/commons/src/components/overflowing_row';
import { RCImage, RCIcon, useMediaQuery } from '@reverbdotcom/cadence/components';
import Tiles from '@reverbdotcom/commons/src/components/tiles';
import { MagnifyingGlassIcon } from '@reverbdotcom/cadence/icons/react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import classNames from 'classnames';
import { reverb_personalization_SearchQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import { V2_MARKETPLACE_PATH } from '../shared/constants';

interface IProps {
  querySuggestions?: reverb_personalization_SearchQuery[];
  loading?: boolean;
  displayPills?: boolean;
  relatedSearchLocation: 'top' | 'bottom';
}

interface QuerySuggestionProps {
  querySuggestions?: reverb_personalization_SearchQuery[];
  loading?: boolean;
  relatedSearchLocation: 'top' | 'bottom';
}

const COMPONENT_NAME = 'MarketplaceRelatedSearches';
const MAX_SEARCHES = 7;

function backgroundImageStyle(suggestion) {
  let imageUrl;
  if (suggestion.listings) {
    imageUrl = suggestion.listings[0].images[0]?.source;
  }
  return imageUrl;
}

function buildClickEvent(querySuggestion, position, displayStyle, relatedSearchLocation) {
  return {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ClickedRelatedSearchSuggestion,
    query: querySuggestion.query,
    relatedSearchLocation,
    displayStyle,
    position,
  };
}

function renderPlaceholder(displayPills, idx = null) {
  const classes = classNames(
    {
      'related-search-pill': displayPills,
      'related-search-pill--placeholder': displayPills,
      'related-search-image-card': !displayPills,
      'related-search-image-card--placeholder': !displayPills,
    },
  );

  if (displayPills) {
    return (
      <div className={classes} key={`related-search-pill__placeholder-${idx}`}>
        <div className="related-search-pill__text" key={`related-search-pill__placeholder-text-${idx}`} />
      </div>
    );
  }

  return (
    <div className={classes} key={`related-search-image-card__placeholder-${idx}`}>
      <div className="related-search-image-card__image-container" key={`related-search-image-card__placeholder-image-container-${idx}`}>
        <div className="related-search-image-card__image" />
      </div>
    </div>
  );
}

function QuerySuggestion({ suggestion, idx, displayPills, relatedSearchLocation }) {
  const url = useURL();
  const urlParams = {
    query: suggestion.query,
    referer: 'related_search',
  };

  Object.keys(urlParams).forEach(k => !urlParams[k] && delete urlParams[k]);

  let path = Paths.marketplace.expand(urlParams);
  if (url.pathname.includes(V2_MARKETPLACE_PATH)) {
    path = Paths.marketplaceV2.expand(urlParams);
  }

  if (displayPills) {
    return (
      <CoreLink
        clickEvent={buildClickEvent(suggestion, idx, 'pill', relatedSearchLocation)}
        className="related-search-pill"
        to={path}
        onClick={() => window.scrollTo(0, 0)}
        key={`related-search-pill-link-${suggestion.query}-${idx}`}
      >
        <div className="related-search-pill__icon">
          <RCIcon svgComponent={MagnifyingGlassIcon} inline />
        </div>
        <div className="related-search-pill__text" key={`related-search-pill__text-${suggestion.query}-${idx}`}>
          {suggestion?.query}
        </div>
      </CoreLink>
    );
  }

  return (
    <CoreLink
      className="related-search-image-card"
      clickEvent={buildClickEvent(suggestion, idx, 'image', relatedSearchLocation)}
      to={path}
      onClick={() => window.scrollTo(0, 0)}
      key={`related-search-image-link-${suggestion.query}-${idx}`}
    >
      <div
        className="related-search-image-card__image-container"
        key={`related-search-image-card-photo-link-${suggestion.query}-${idx}`} >
        <RCImage
          src={backgroundImageStyle(suggestion)}
          overlay="offwhite"
          borderRadius="full"
        />
      </div>
      <div className="related-search-image-card__text" key={`related-search-image-card-text-${suggestion.query}-${idx}`}>
        {suggestion?.query}
      </div>
    </CoreLink>
  );
}

export function RelatedSearchImageCard({
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: QuerySuggestionProps) {
  return (
    <div className="pt-10">
      <OverflowingRow
        title={
          <div className="related-search-image-card__title" key="related-search-image-card-title">
            {I18n.t('discovery.marketplace.querySuggestions.relatedSearches')}
          </div>
        }
        collectionCount={querySuggestions?.length}
        centeredTitle={!useMediaQuery('mobile')}
      >
        <Tiles
          loading={loading}
          placeholder={renderPlaceholder(false)}
          centered
          singleRow
        >
          {!!querySuggestions?.length &&
            querySuggestions?.map((qs, idx) => {
              return (
                <QuerySuggestion
                  key={`marketplace-related-search-image-card-${idx}`}
                  suggestion={qs}
                  idx={idx}
                  displayPills={false}
                  relatedSearchLocation={relatedSearchLocation}
                />
              );
            })}
        </Tiles>
      </OverflowingRow>

    </div>
  );
}

export function RelatedSearchPills({
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: QuerySuggestionProps) {

  return (
    <div className="related-search-pill__container" key="related-search-pills-container">
      <div className="related-search-pill__title" key="related-search-pills-title">
        {I18n.t('discovery.marketplace.querySuggestions.relatedSearches')}
      </div>
      <div className="related-searches-pills-row" key="related-search-pills-row">
        {!loading && querySuggestions?.map((qs, idx) => {
          return (
            <QuerySuggestion
              key={`marketplace-related-search-pill-${qs?.query}-${idx}`}
              suggestion={qs}
              idx={idx}
              relatedSearchLocation={relatedSearchLocation}
              displayPills
            />
          );
        })}
        {loading && times(MAX_SEARCHES, (idx) => { return renderPlaceholder(true, idx); })}
      </div>
    </div>
  );
}


export function MarketplaceRelatedSearches({
  displayPills = false,
  loading = false,
  querySuggestions = [],
  relatedSearchLocation,
}: IProps) {
  const [showChild, setShowChild] = React.useState(false);
  React.useEffect(() => {
    setShowChild(true);
  }, []);

  if (!loading && !querySuggestions?.length) { return null; }

  const classes = classNames(
    'related-searches-row',
    {
      'related-searches-pills': displayPills,
      'related-searches-image-cards': !displayPills,
    },
  );

  if (!showChild) {
    if (displayPills) {
      return (
        <div className={classes} key={`${relatedSearchLocation}-pills-related-searches-row-placeholder`}>
          <RelatedSearchPills
            loading
            querySuggestions={[]}
            relatedSearchLocation={relatedSearchLocation}
          />
        </div>
      );
    }

    return (
      <div className={classes} key={`${relatedSearchLocation}-image-cards-related-searches-row-placeholder`}>
        <RelatedSearchImageCard
          querySuggestions={[]}
          relatedSearchLocation={relatedSearchLocation}
          loading
        />
      </div>
    );
  }

  return (
    <div
      className={classes}
      key={`${relatedSearchLocation}-${displayPills ? 'pills' : 'image-cards'}-related-searches-row`}
    >
      {displayPills ?
        <RelatedSearchPills
          loading={loading}
          querySuggestions={querySuggestions}
          relatedSearchLocation={relatedSearchLocation}
        />
        :
        <RelatedSearchImageCard
          loading={loading}
          querySuggestions={querySuggestions}
          relatedSearchLocation={relatedSearchLocation}
        />
      }
    </div>
  );
}

export default MarketplaceRelatedSearches;
